import { adminMode, readerSettings } from '$lib/stores';
import type { ReaderSettings } from '$types/bible';
import type { UserInfo } from '$types/interfaces';
import { Collections, type UsersResponse } from '$types/pocketbase';
import { get } from 'svelte/store';
import { currentUser, pbClient } from '$lib/client/pocketbase';

export const login = async (user: string, password: string) => {
  const response = await pbClient
    .collection(Collections.Users)
    .authWithPassword<UsersResponse<ReaderSettings>>(user, password);

  if (response.record.settings) readerSettings.set(response.record.settings);
  return response;
};

export function logout() {
  pbClient.authStore.clear();
  document.cookie = 'pb_auth=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
  adminMode.set(false);
}

export async function requestVerification(email: string) {
  await pbClient.collection(Collections.Users).requestVerification(email);
}

export async function updateUserData(user: UserInfo): Promise<UserInfo | null> {
  if (!pbClient.authStore.isValid) throw new Error('Not logged in');
  const response = await pbClient
    .collection(Collections.Users)
    .update<UsersResponse<ReaderSettings>>(user.id, {
      name: user.name,
      surname: user.surname,
      phone: user.phone,
      address: user.address
    })
    .catch(console.error);

  if (!response) return null;
  if (response) await pbClient.collection(Collections.Users).authRefresh();
  return {
    name: response.name,
    surname: response.surname,
    phone: response.phone,
    address: response.address,
    email: response.email,
    username: response.username,
    id: response.id
  };
}

export async function requestPasswordReset(email: string) {
  await pbClient.collection(Collections.Users).requestPasswordReset(email);
}

export async function resetPassword(
  token: string,
  password: string,
  passwordConfirm: string
): Promise<boolean | null> {
  const response = await pbClient
    .collection(Collections.Users)
    .confirmPasswordReset(token, password, passwordConfirm)
    .catch(console.error);
  if (!response) return null;
  return true;
}

export async function saveSettings(settings: ReaderSettings): Promise<ReaderSettings> {
  if (!pbClient.authStore.isValid || !get(currentUser)) throw new Error('Not logged in');
  const response = await pbClient
    .collection(Collections.Users)
    .update<UsersResponse<ReaderSettings>>(get(currentUser)?.id, {
      settings: settings
    });

  if (response.settings) return response.settings;
  throw new Error('Settings not saved');
}
